'use client';

import type React from 'react';
import type { ProductAttributeT } from '../../../../../type/product/productAttributes';
import type { ProductToCompareT } from '../../../../../type/product/productComparison';

import {
  ProductComparisonGroup,
  ProductComparisonGroupContent,
  ProductComparisonGroupLabel,
  ProductComparisonGroupSpacer,
  ProductComparisonGroupState,
  ProductComparisonGroupToggle,
  productComparisonGroupChevronVariants,
} from '@vs-shop/commerce-ui/productComparison';
import { ChevronDown } from 'lucide-react';
import { useIntl } from 'react-intl';

import ProductComparisonAttributeRows from './ProductComparisonAttributeRows';

type Props = {
  group: {
    id: string;
    name: string | null;
  };
  products: readonly ProductToCompareT[];
  attributes: ProductAttributeT[];
  isOpen: boolean;
  onToggleAction: () => void;
};

export default function ProductComparisonAttributeGroupRow({
  group,
  products,
  attributes,
  isOpen,
  onToggleAction,
}: Props) {
  const intl = useIntl();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleAction();
    }
  };

  const state = isOpen
    ? ProductComparisonGroupState.EXPANDED
    : ProductComparisonGroupState.COLLAPSED;

  return (
    <ProductComparisonGroup open={isOpen}>
      {/* Group Header Row */}
      <ProductComparisonGroupToggle
        onClick={onToggleAction}
        aria-expanded={isOpen}
        aria-controls={`group-${group.id}-content`}
        data-group-id={group.id}
      >
        <ProductComparisonGroupLabel>
          {group.name ??
            intl.formatMessage({
              id: 'productsComparison.attributeGroup.defaultName',
              defaultMessage: 'Parametry',
            })}
        </ProductComparisonGroupLabel>
        <ChevronDown
          className={productComparisonGroupChevronVariants({ state })}
          aria-hidden="true"
        />
      </ProductComparisonGroupToggle>

      {/* Product Column Cells (clickable to toggle) */}
      {products.map((product) => (
        <ProductComparisonGroupSpacer
          key={product.code}
          onClick={onToggleAction}
          aria-expanded={isOpen}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        />
      ))}

      {/* Collapsible Content (Attribute Rows) */}
      <ProductComparisonGroupContent id={`group-${group.id}-content`}>
        <ProductComparisonAttributeRows attributes={attributes} products={products} />
      </ProductComparisonGroupContent>
    </ProductComparisonGroup>
  );
}
